import { useState, useEffect } from 'react';

const PRODUCT_AVAILABILITY_QUERY = `
  query getProductAvailability($handle: String!) {
    product(handle: $handle) {
      id
      title
      availableForSale
      variants(first: 100) {
        edges {
          node {
            id
            availableForSale
          }
        }
      }
    }
    localization {
      availableCountries {
        isoCode
        name
        currency {
          isoCode
          name
          symbol
        }
      }
    }
  }
`;

export const useProductCountryAvailability = (productHandle) => {
    const [availableCountries, setAvailableCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAvailableInMultipleCountries, setIsAvailableInMultipleCountries] = useState(false);

    useEffect(() => {
        if (!productHandle || typeof window === 'undefined') {
            setLoading(false);
            return;
        }

        const fetchProductAvailability = async () => {
            try {
                const response = await fetch(
                    `https://lineage-journey.myshopify.com/api/2024-01/graphql.json`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Shopify-Storefront-Access-Token': 'e3e6f7f2ffec6ef5ba855eaaeb608d83',
                        },
                        body: JSON.stringify({
                            query: PRODUCT_AVAILABILITY_QUERY,
                            variables: { handle: productHandle }
                        }),
                    }
                );

                const result = await response.json();

                if (result.errors) {
                    throw new Error(result.errors[0].message);
                }

                const product = result.data?.product;
                const countries = result.data?.localization?.availableCountries || [];

                if (product && product.availableForSale && countries.length > 0) {
                    // Product is available and store ships to multiple countries
                    setAvailableCountries(countries);
                    setIsAvailableInMultipleCountries(countries.length > 1);
                } else {
                    setAvailableCountries([]);
                    setIsAvailableInMultipleCountries(false);
                }
            } catch (err) {
                console.error('Error fetching product country availability:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchProductAvailability();
    }, [productHandle]);

    return {
        availableCountries,
        isAvailableInMultipleCountries,
        loading,
        error,
    };
};

export const useProductCurrencies = (productHandle) => {
    const { availableCountries, isAvailableInMultipleCountries, loading, error } =
        useProductCountryAvailability(productHandle);
    const [currencies, setCurrencies] = useState([]);

    useEffect(() => {
        if (availableCountries.length > 0) {
            const currenciesMap = new Map();

            availableCountries.forEach(country => {
                if (!currenciesMap.has(country.currency.isoCode)) {
                    currenciesMap.set(country.currency.isoCode, {
                        isoCode: country.currency.isoCode,
                        name: country.currency.name,
                        symbol: country.currency.symbol,
                        countries: [],
                    });
                }
                currenciesMap.get(country.currency.isoCode).countries.push({
                    isoCode: country.isoCode,
                    name: country.name,
                });
            });

            setCurrencies(Array.from(currenciesMap.values()));
        }
    }, [availableCountries]);

    return {
        currencies,
        isAvailableInMultipleCountries,
        totalCountries: availableCountries.length,
        loading,
        error,
    };
};